import {
  Button,
  Dropdown,
  Image,
  Input,
  Progress,
  Select,
  Space,
  TableProps,
} from "antd";
import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import ActionIcon from "../../../assets/icons/ActionIcon";
import AddIcon from "../../../assets/icons/AddIcon";
import ArchivedIcon from "../../../assets/icons/ArchivedIcon";
import CheckCircleIcon from "../../../assets/icons/CheckCircleIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import placeholder from "../../../assets/images/placeholder.png";
import TitleBar from "../../../components/common/TitleBar";
import ConfirmModal from "../../../components/ui/ConfirmModal";
import CustomTable from "../../../components/ui/CustomTable";
import {
  useArchiveVendorMutation,
  useGetVendorListQuery,
  useUpdateVendorMutation,
} from "../../../features/Vendor/vendor.api";
import { VendorDetails } from "../../../features/Vendor/vendor.type";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { formatDate } from "../../../lib/time";
import { toast } from "../../../lib/toast";
import colors from "../../../theme/colors";
import { countParPageSerialNumber } from "../../../utils/number";
import { capitalize, valueOrNa } from "../../../utils/string";
import { addKeyInArray, selectSearchOption } from "../../../utils/utils";

const STATUS_CONFIG = {
  ACTIVE: "status status-success",
  ARCHIVED: "status status-error",
  DRAFT: "status status-slate",
};

type ModalType = "ARCHIVE" | "ACTIVE";

const VendorList = () => {
  const navigate = useNavigate();
  const { open, handleOpen, handleClose } = useModal<unknown, ModalType>();
  const isActiveModal = open.type === "ACTIVE";
  const {
    page,
    limit,
    filter,
    keyword,
    handleFilter,
    handleLimit,
    handlePagination,
    handleSearch,
  } = useTable<{
    status: string;
  }>({});

  const {
    data: vendorResponse,
    isLoading,
    isFetching,
  } = useGetVendorListQuery({
    params: { page, limit, search: keyword, ...filter },
  });

  // ------ Mutations ------
  const [
    archiveVendor,
    {
      isLoading: archiving,
      isError: isArchiveError,
      error: archiveError,
      isSuccess: archived,
    },
  ] = useArchiveVendorMutation();
  const [
    updateVendor,
    {
      isLoading: updating,
      isSuccess: updated,
      isError: isUpdateError,
      error: updateError,
    },
  ] = useUpdateVendorMutation();

  // ------ Effects ------
  useEffect(() => {
    if (archived) {
      toast("success", "Vendor archived successfully.");
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archived]);
  useEffect(() => {
    if (updated) {
      toast("success", "Vendor activated successfully.");
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updated]);

  // ------ Errors ------
  useError(isArchiveError, archiveError, "Vendor archive failed.");
  useError(isUpdateError, updateError, "Vendor active failed.");

  const columns: TableProps<VendorDetails>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Vendor ID",
      dataIndex: "vendor_code",
      render: (val) => valueOrNa(val),
    },
    {
      title: "Vendor Name",
      render: (val) => {
        return (
          <Space>
            <Image
              preview={false}
              src={val?.logo || placeholder}
              height={32}
              width={32}
              className="rounded-full"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).src = placeholder;
              }}
            />
            <span className="font-medium text-theme-gray-24">
              {val?.business_name}
            </span>
          </Space>
        );
      },
    },
    {
      title: "Location",
      dataIndex: "full_address",
      render: (val) => valueOrNa(val),
    },
    {
      title: "Profile Completion",
      dataIndex: "profile_completion",
      key: "profile_completion",
      render: (value: number) => (
        <div className="flex items-center gap-2">
          <Progress
            percent={value}
            size="small"
            showInfo={false}
            strokeColor={colors.theme.success[500]} // Tailwind green-600
            className="w-[120px]"
          />
          <span>{value}%</span>
        </div>
      ),
    },
    {
      title: "Onboard Date",
      dataIndex: "created_at",
      render: (val) => formatDate(val),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (val) => {
        return (
          <span
            className={
              STATUS_CONFIG[
                String(val).toUpperCase() as keyof typeof STATUS_CONFIG
              ]
            }
          >
            {capitalize(val)}
          </span>
        );
      },
    },
    {
      title: "Action",
      align: "center",
      width: 130,
      render: (vendor) => {
        const { id, status, business_name } = vendor || {};
        return (
          <Space align="center">
            <Dropdown
              menu={{
                items: [
                  ...(status === "archived"
                    ? [
                        {
                          key: "active",
                          label: (
                            <Space>
                              <CheckCircleIcon height={16} width={16} />
                              <span>Active</span>
                            </Space>
                          ),
                          onClick: () =>
                            handleOpen({
                              type: "ACTIVE",
                              data: { id, business_name },
                            }),
                        },
                      ]
                    : []),

                  ...(status === "draft"
                    ? [
                        {
                          key: "active",
                          label: (
                            <Space>
                              <CheckCircleIcon height={16} width={16} />
                              <span>Active</span>
                            </Space>
                          ),
                          onClick: () =>
                            handleOpen({
                              type: "ACTIVE",
                              data: { id, business_name },
                            }),
                        },
                        {
                          key: "view",
                          label: (
                            <Space>
                              <ViewIcon />
                              <span>View</span>
                            </Space>
                          ),
                          onClick: () => navigate(`/admin/vendors/view/${id}`),
                        },
                        {
                          key: "archive",
                          label: (
                            <Space>
                              <ArchivedIcon />
                              <span>Archive</span>
                            </Space>
                          ),
                          onClick: () =>
                            handleOpen({
                              type: "ARCHIVE",
                              data: { id, business_name },
                            }),
                        },
                      ]
                    : []),

                  ...(status === "active"
                    ? [
                        {
                          key: "view",
                          label: (
                            <Space>
                              <ViewIcon />
                              <span>View</span>
                            </Space>
                          ),
                          onClick: () => navigate(`/admin/vendors/view/${id}`),
                        },
                        {
                          key: "archive",
                          label: (
                            <Space>
                              <ArchivedIcon />
                              <span>Archive</span>
                            </Space>
                          ),
                          onClick: () =>
                            handleOpen({
                              type: "ARCHIVE",
                              data: { id, business_name },
                            }),
                        },
                      ]
                    : []),
                ],
              }}
              placement="bottomRight"
              trigger={["click"]}
              overlayClassName="w-[150px]"
            >
              <Button className="size-8 p-0">
                <ActionIcon />
              </Button>
            </Dropdown>
          </Space>
        );
      },
    },
  ];
  return (
    <SidebarLayout title="Vendor Management">
      <TitleBar
        title="Vendor Registration"
        subTitle="Manage vendor registration and profile information"
      >
        <Link to="/admin/vendors/add">
          <Button icon={<AddIcon />} className="button-primary">
            Add New
          </Button>
        </Link>
      </TitleBar>

      <CustomTable
        title={"Vendor List"}
        loading={isLoading || isFetching}
        limit={limit}
        page={page}
        columns={columns}
        total={vendorResponse?.data?.totalLength}
        dataSource={addKeyInArray(vendorResponse?.data?.vendors)}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        hideDifferentRowColor
        filterSection={
          <>
            <Input
              className="bg-input"
              suffix={<SearchIcon />}
              placeholder="Search by name"
              style={{ minWidth: 170, maxWidth: 180 }}
              onChange={(val) => handleSearch(val)}
            />
            <Select
              allowClear
              placeholder="Status"
              placement="bottomRight"
              filterOption={selectSearchOption}
              options={[
                { label: "Active", value: "active" },
                { label: "Draft", value: "draft" },
                { label: "Archived", value: "archived" },
              ]}
              className="min-w-[90px] bg-select"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
              onChange={(val) => handleFilter("status", val)}
              popupMatchSelectWidth={120}
            />
          </>
        }
      />

      <ConfirmModal
        closable
        icon={isActiveModal ? "WARNING" : "ARCHIVE"}
        okText={isActiveModal ? "Yes, Active" : "Yes, Archive"}
        confirmTitle={isActiveModal ? "Active Vendor" : "Archive Vendor"}
        questionText={`Are you sure you want to ${
          isActiveModal ? "active" : "archive"
        } “${
          (open.data as { business_name: string })?.business_name
        }” this will set their status to  ${
          isActiveModal ? "Active" : "Archive"
        }.`}
        open={open.open}
        okLoading={archiving || updating}
        handleClose={handleClose}
        handleOk={() => {
          if (isActiveModal) {
            updateVendor({
              id: (open.data as { id: number })?.id,
              data: { status: "active" },
            });
            console.log("Updating");
          } else {
            archiveVendor({ id: (open.data as { id: number })?.id });
          }
        }}
      />
    </SidebarLayout>
  );
};

export default VendorList;
